import { normalizarGraus } from '../vento'

/**
 * Pluma gaussiana — dispersão de gás neutro, fonte contínua, terreno plano.
 *
 *   C(x,y,z) = Q / (2π·u·σy·σz) · exp(−y²/2σy²) · [exp(−(z−H)²/2σz²) + exp(−(z+H)²/2σz²)]
 *
 * x a favor do vento, y transversal, z altura; H altura da fonte; o segundo
 * termo da soma é a "imagem" que representa a reflexão no solo. σy e σz são
 * as correlações de Briggs (1973) para a classe de estabilidade de Pasquill.
 *
 * Limites que o aluno precisa saber, e a interface repete:
 * - gás denso (cloro, GLP, amônia que sai líquida) escoa junto ao solo e
 *   entra em baixadas e galerias — o modelo gaussiano subestima isso;
 * - as correlações de Briggs valem de 100 m a 10 km; perto da fonte são
 *   extrapolação;
 * - vento e estabilidade constantes, sem prédios, sem relevo.
 */

export type ClasseEstabilidade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
export type Terreno = 'aberto' | 'urbano'
export type Insolacao = 'forte' | 'moderada' | 'fraca' | 'encoberto'
export type Nebulosidade = 'nublado' | 'poucaNuvem'

export const CLASSES: readonly ClasseEstabilidade[] = ['A', 'B', 'C', 'D', 'E', 'F']

export const DESCRICAO_CLASSE: Record<ClasseEstabilidade, string> = {
  A: 'muito instável — sol forte, pouco vento: a pluma se abre e sobe rápido',
  B: 'instável',
  C: 'levemente instável',
  D: 'neutra — céu encoberto ou vento forte',
  E: 'levemente estável',
  F: 'estável — noite limpa, pouco vento: a pluma fica estreita e rente ao chão, e vai longe',
}

export type CondicaoMeteorologica =
  | { ventoMS: number; periodo: 'dia'; insolacao: Insolacao }
  | { ventoMS: number; periodo: 'noite'; nebulosidade: Nebulosidade }

/**
 * Tabela de Pasquill (Turner 1970). Cada célula traz uma ou duas classes; as
 * faixas intermediárias ("B–C") são resolvidas para a MAIS ESTÁVEL, que dá
 * concentração maior mais longe — a escolha conservadora para ensino.
 * Noite com vento abaixo de 2 m/s não tem classe na tabela original; usa-se F.
 */
const TABELA_DIA: ReadonlyArray<readonly [number, Record<Exclude<Insolacao, 'encoberto'>, ClasseEstabilidade>]> = [
  [2, { forte: 'A', moderada: 'B', fraca: 'B' }], // A, A–B, B
  [3, { forte: 'B', moderada: 'B', fraca: 'C' }], // A–B, B, C
  [5, { forte: 'B', moderada: 'C', fraca: 'C' }], // B, B–C, C
  [6, { forte: 'C', moderada: 'D', fraca: 'D' }], // C, C–D, D
  [Infinity, { forte: 'C', moderada: 'D', fraca: 'D' }],
]

const TABELA_NOITE: ReadonlyArray<readonly [number, Record<Nebulosidade, ClasseEstabilidade>]> = [
  [2, { nublado: 'F', poucaNuvem: 'F' }],
  [3, { nublado: 'E', poucaNuvem: 'F' }],
  [5, { nublado: 'D', poucaNuvem: 'E' }],
  [Infinity, { nublado: 'D', poucaNuvem: 'D' }],
]

export function classeEstabilidade(condicao: CondicaoMeteorologica): ClasseEstabilidade {
  if (condicao.periodo === 'dia') {
    if (condicao.insolacao === 'encoberto') return 'D'
    const linha = TABELA_DIA.find(([limite]) => condicao.ventoMS < limite)!
    return linha[1][condicao.insolacao]
  }
  const linha = TABELA_NOITE.find(([limite]) => condicao.ventoMS < limite)!
  return linha[1][condicao.nebulosidade]
}

/** Coeficientes de Briggs: σ = a·x·(1 + b·x)^p. */
type Coeficiente = readonly [a: number, b: number, p: number]

const BRIGGS: Record<Terreno, Record<ClasseEstabilidade, { y: Coeficiente; z: Coeficiente }>> = {
  // Briggs (1973), campo aberto
  aberto: {
    A: { y: [0.22, 0.0001, -0.5], z: [0.2, 0, 1] },
    B: { y: [0.16, 0.0001, -0.5], z: [0.12, 0, 1] },
    C: { y: [0.11, 0.0001, -0.5], z: [0.08, 0.0002, -0.5] },
    D: { y: [0.08, 0.0001, -0.5], z: [0.06, 0.0015, -0.5] },
    E: { y: [0.06, 0.0001, -0.5], z: [0.03, 0.0003, -1] },
    F: { y: [0.04, 0.0001, -0.5], z: [0.016, 0.0003, -1] },
  },
  // Briggs urbano (McElroy-Pooler), EPA ISC3 Tabelas 1-3 e 1-4
  urbano: {
    A: { y: [0.32, 0.0004, -0.5], z: [0.24, 0.001, 0.5] },
    B: { y: [0.32, 0.0004, -0.5], z: [0.24, 0.001, 0.5] },
    C: { y: [0.22, 0.0004, -0.5], z: [0.2, 0, 1] },
    D: { y: [0.16, 0.0004, -0.5], z: [0.14, 0.0003, -0.5] },
    E: { y: [0.11, 0.0004, -0.5], z: [0.08, 0.0015, -0.5] },
    F: { y: [0.11, 0.0004, -0.5], z: [0.08, 0.0015, -0.5] },
  },
}

const sigma = ([a, b, p]: Coeficiente, x: number) => a * x * Math.pow(1 + b * x, p)

export function sigmas(classe: ClasseEstabilidade, xM: number, terreno: Terreno): { sigmaYM: number; sigmaZM: number } {
  const coef = BRIGGS[terreno][classe]
  return { sigmaYM: sigma(coef.y, xM), sigmaZM: sigma(coef.z, xM) }
}

export interface Pluma {
  vazaoKgS: number
  /** Velocidade do vento na altura de referência (10 m), m/s. */
  ventoMS: number
  classe: ClasseEstabilidade
  terreno: Terreno
  alturaFonteM: number
}

/** Concentração em kg/m³ no ponto (x a favor do vento, y transversal, z altura). */
export function concentracaoKgM3(pluma: Pluma, xM: number, yM: number, zM: number): number {
  if (xM <= 0 || pluma.vazaoKgS <= 0 || pluma.ventoMS <= 0) return 0
  const { sigmaYM, sigmaZM } = sigmas(pluma.classe, xM, pluma.terreno)
  const H = pluma.alturaFonteM
  const transversal = Math.exp(-(yM * yM) / (2 * sigmaYM * sigmaYM))
  const vertical =
    Math.exp(-((zM - H) ** 2) / (2 * sigmaZM * sigmaZM)) + Math.exp(-((zM + H) ** 2) / (2 * sigmaZM * sigmaZM))
  return (pluma.vazaoKgS / (2 * Math.PI * pluma.ventoMS * sigmaYM * sigmaZM)) * transversal * vertical
}

/** Primeiro x avaliado: dentro do primeiro metro a gaussiana pontual não tem sentido físico. */
const X_INICIAL_M = 1
const X_LIMITE_M = 50_000

/**
 * Distância a favor do vento em que a concentração no eixo, no solo, cai para
 * o limiar. Fonte no solo: a concentração no eixo só decresce com x. Fonte
 * elevada: a busca parte do pico.
 */
export function alcanceMaximoM(pluma: Pluma, limiarKgM3: number): number | null {
  const noEixo = (x: number) => concentracaoKgM3(pluma, x, 0, 0)
  // acha o pico (fonte elevada) numa varredura logarítmica
  let xPico = X_INICIAL_M
  for (let x = X_INICIAL_M; x < X_LIMITE_M; x *= 1.15) if (noEixo(x) > noEixo(xPico)) xPico = x
  if (noEixo(xPico) < limiarKgM3) return null
  let perto = xPico
  let longe = xPico
  while (noEixo(longe) > limiarKgM3 && longe < X_LIMITE_M) longe *= 2
  if (longe >= X_LIMITE_M) return X_LIMITE_M
  for (let i = 0; i < 80 && longe - perto > 0.01; i++) {
    const meio = (perto + longe) / 2
    if (noEixo(meio) > limiarKgM3) perto = meio
    else longe = meio
  }
  return (perto + longe) / 2
}

/**
 * Borda do contorno de concentração no solo, como lista de [x, meia-largura]
 * da fonte até o alcance máximo. Simétrico em y: quem desenha espelha.
 */
export function contornoNoSolo(pluma: Pluma, limiarKgM3: number, pontos = 60): Array<[number, number]> {
  const alcance = alcanceMaximoM(pluma, limiarKgM3)
  if (alcance == null) return []
  const borda: Array<[number, number]> = []
  for (let i = 0; i <= pontos; i++) {
    // distribuição mais densa perto da fonte e da ponta, onde a forma muda mais
    const t = i / pontos
    const x = X_INICIAL_M + (alcance - X_INICIAL_M) * (0.5 - 0.5 * Math.cos(Math.PI * t))
    const eixo = concentracaoKgM3(pluma, x, 0, 0)
    const { sigmaYM } = sigmas(pluma.classe, x, pluma.terreno)
    const meiaLargura = eixo > limiarKgM3 ? sigmaYM * Math.sqrt(2 * Math.log(eixo / limiarKgM3)) : 0
    borda.push([x, meiaLargura])
  }
  return borda
}

/**
 * Ponto da cena (x leste, z sul) em coordenadas da pluma: [a favor do vento,
 * transversal]. A fonte está na origem.
 */
export function paraCoordenadasDaPluma(direcaoVentoGraus: number, xCena: number, zCena: number): readonly [number, number] {
  const rad = (normalizarGraus(direcaoVentoGraus) * Math.PI) / 180
  const dx = -Math.sin(rad)
  const dz = Math.cos(rad)
  return [xCena * dx + zCena * dz, -xCena * dz + zCena * dx]
}

/**
 * Maior concentração no solo entre pontos da cena (centro e cantos de um
 * prédio, por exemplo), em kg/m³, e a distância a favor do vento do ponto
 * mais exposto — que dá o tempo de chegada da pluma.
 */
export function concentracaoMaximaNosPontos(
  pluma: Pluma,
  direcaoVentoGraus: number,
  pontos: ReadonlyArray<readonly [number, number]>,
): { kgM3: number; xM: number } {
  let melhor = { kgM3: 0, xM: Infinity }
  for (const [xCena, zCena] of pontos) {
    const [x, y] = paraCoordenadasDaPluma(direcaoVentoGraus, xCena, zCena)
    const c = concentracaoKgM3(pluma, x, y, 0)
    if (c > melhor.kgM3 || (melhor.kgM3 === 0 && x > 0 && x < melhor.xM)) melhor = { kgM3: c, xM: x }
  }
  return melhor
}

/** Coeficiente de descarga de orifício de aresta viva (CCPS 2000). */
export const COEFICIENTE_DESCARGA = 0.61
const GRAVIDADE = 9.81
const PRESSAO_ATMOSFERICA_KPA = 101.325

/**
 * Vazão de líquido por um furo, Bernoulli: Q = Cd·A·√(2ρ·ΔP), com ΔP a
 * sobrepressão do vapor mais a coluna de líquido acima do furo.
 *
 * Para gás liquefeito, considerar que toda essa vazão vira nuvem é
 * conservador: parte "flasheia", parte vira aerossol, parte pode empoçar.
 */
export function vazaoPorFuroKgS({
  diametroFuroMm,
  densidadeLiquidoKgM3,
  pressaoVaporKPa,
  colunaLiquidoM = 0,
}: {
  diametroFuroMm: number
  densidadeLiquidoKgM3: number
  pressaoVaporKPa: number
  colunaLiquidoM?: number
}): number {
  const area = Math.PI * (diametroFuroMm / 2000) ** 2
  const deltaPPa = Math.max(0, (pressaoVaporKPa - PRESSAO_ATMOSFERICA_KPA) * 1000) + densidadeLiquidoKgM3 * GRAVIDADE * colunaLiquidoM
  if (deltaPPa <= 0) return 0
  return COEFICIENTE_DESCARGA * area * Math.sqrt(2 * densidadeLiquidoKgM3 * deltaPPa)
}
